/**
 * Чек-лист обращения (`voicelab/checklist`) — в карточке разговора AI Lab (бот @urtoperatorbot) и в панели
 * кнопки AI «Чатов»: пункты сценария с состоянием (✓ пройден · … черновик ждёт отправки · ✗ ещё нет), значение
 * и где отмечено. Видно, что модель поговорила, но, например, не поставила запись в CRM.
 * Спека `docs/specs/2026-11-04-ai-checklists-design.md`.
 */
import type { LabChecklist, LabChecklistItem } from "@/lib/voiceLab";

const ЗНАК = { done: "✓", pending: "…", none: "✗" } as const;
const ЦВЕТ = { done: "text-ok", pending: "text-warn", none: "text-fg-muted" } as const;

/** «2026-11-03 12:00» → «03.11 12:00». */
function дата(s: string): string {
  return `${s.slice(8, 10)}.${s.slice(5, 7)} ${s.slice(11, 16)}`;
}

/** Где отмечено: реплика бота, ждущий черновик «Чатов» или система (действие CRM, ссылка). */
function где(п: LabChecklistItem): string {
  if (п.state === "pending") return "черновик ждёт отправки";
  if (п.turn != null) return `реплика ${п.turn + 1}`;
  if (п.state === "done" && п.source === "code") return "система";
  return "";
}

export function ChecklistBox({
  checklist,
  className = "",
}: {
  checklist: LabChecklist | null | undefined;
  className?: string;
}) {
  if (!checklist) return null;
  const пройдено = checklist.items.filter((п) => п.state === "done").length;
  return (
    <section
      className={`flex flex-col gap-1 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2.5 py-2 text-[12.5px] ${className}`}
      data-annot="чек-лист обращения"
    >
      <div className="flex flex-wrap items-baseline gap-x-2">
        <span className="font-medium">Чек-лист обращения «{checklist.title}»</span>
        <span className="text-fg-muted">· {checklist.version}</span>
        {checklist.since && (
          <span className="text-fg-muted" title="Начало обращения: первое сообщение клиента">
            · с {дата(checklist.since)}
          </span>
        )}
        <span className="ml-auto tabular-nums text-fg-muted">
          {пройдено} из {checklist.items.length}
        </span>
      </div>
      <ul className="flex flex-col gap-0.5">
        {checklist.items.map((п) => {
          const место = где(п);
          return (
            <li key={п.code} className="flex items-baseline gap-1.5">
              <span className={`w-3 flex-none text-center font-medium ${ЦВЕТ[п.state]}`}>{ЗНАК[п.state]}</span>
              <span className="min-w-0 break-words">
                <span className={п.state === "none" ? "text-fg-muted" : ""}>{п.title}</span>
                {п.value && п.state !== "none" && <span>: {п.value}</span>}
                {место && <span className="text-fg-muted"> · {место}</span>}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
